import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { studyMaterialCountBadge } from "@lms/backend/lib/lessons/study-material-labels";
import {
  ConfirmDeleteDialog,
  type StudentDataCounts,
} from "@/components/instructor/confirm-delete-dialog";
import { useCourseLabels } from "@/components/instructor/course-labels";
import { LessonVideoUpload } from "@/components/instructor/lesson-video-upload";
import { apiErrorText, readApiErrorResponse } from "@/lib/api-error";
import { formatLocalDateTime } from "@/lib/datetime-local";
import { apiFetch } from "@/services/http/api-fetch";
import type { ServerVideoUpload } from "@/services/api/video-upload-store";

export type CurriculumLesson = {
  id: string;
  title: string;
  subtitle: string | null;
  contentType: string;
  sortOrder: number;
  videoProvider: string;
  videoStatus: string;
  hasVideo: boolean;
  /** R-3: completable / holds students' completion until its video is ready / doesn't count. */
  completionState?: "completable" | "awaiting_video" | "not_counted";
  hasBodyHtml: boolean;
  attachmentCount: number;
  unlockAt: string | null;
  /** CRS-2: an upload Bunny has not finished with, from the builder GET. */
  videoUpload?: ServerVideoUpload | null;
};

export type CurriculumModule = {
  id: string;
  title: string;
  sortOrder: number;
  lessons: CurriculumLesson[];
};

type DeleteTarget =
  | { kind: "module"; moduleId: string; name: string }
  | { kind: "lesson"; moduleId: string; lessonId: string; name: string };

type Props = {
  courseId: string;
  modules: CurriculumModule[];
  /** Session search text; while set, the list is filtered and reordering is off. */
  filter: string;
  uploadRunning: boolean;
  /** FR-14 a: the school has no Bunny Stream library yet; show why instead of the control. */
  uploadNotConfigured?: boolean;
  /** Id of the "uploading converts a text session" note, for text sessions' upload inputs. */
  convertsNoteId: string;
  onSelectModule: (moduleId: string) => void;
  onEditLesson: (moduleId: string, lesson: CurriculumLesson) => void;
  /** The new order, shown at once; the server has it already. */
  onModulesChange: (modules: CurriculumModule[]) => void;
  /** Reload from the server (after a rename, a delete or a failed reorder). */
  onChanged: () => Promise<void> | void;
  onDeleted?: (target: { moduleId: string; lessonId?: string }) => void;
};

/**
 * R-3 (LEARN-6): tells the instructor when a session holds every student's course completion (its
 * video is uploading, encoding or failed) or doesn't count toward it (a video session with no
 * video and nothing on its way) — the same rule the completion count uses.
 */
function LessonCompletionNote({ lesson }: { lesson: CurriculumLesson }) {
  const t = useTranslations("instructor");
  if (lesson.completionState === "awaiting_video") {
    const failed = lesson.videoStatus === "failed";
    return (
      <span
        className="ml-2 rounded bg-[var(--warning-soft)] px-1.5 py-0.5 text-xs text-[var(--text-primary)]"
        data-testid={`lesson-completion-note-${lesson.id}`}
      >
        {failed ? t("lessonVideoFailedHolds") : t("lessonVideoAwaitedHolds")}
      </span>
    );
  }
  if (lesson.completionState === "not_counted" && lesson.contentType === "video") {
    return (
      <span
        className="ml-2 text-xs text-[var(--text-secondary)]"
        data-testid={`lesson-completion-note-${lesson.id}`}
      >
        {t("lessonNoVideoNotCounted")}
      </span>
    );
  }
  return null;
}

function swap<T>(items: T[], index: number, offset: -1 | 1): T[] {
  const next = [...items];
  const other = index + offset;
  [next[index], next[other]] = [next[other]!, next[index]!];
  return next;
}

/**
 * F1 (CRS-4): the curriculum — modules and their sessions — with inline rename, delete (guarded
 * by the students'-data check) and up/down reordering. No drag-and-drop library: two buttons per
 * row work with a keyboard and a screen reader as they are.
 */
export function CurriculumModuleList({
  courseId,
  modules,
  filter,
  uploadRunning,
  uploadNotConfigured = false,
  convertsNoteId,
  onSelectModule,
  onEditLesson,
  onModulesChange,
  onChanged,
  onDeleted,
}: Props) {
  const t = useTranslations("instructor");
  const tRoot = useTranslations();
  const locale = useLocale();
  const label = useCourseLabels();
  const now = Date.now();
  const errorText = (err: unknown, fallbackKey: string) =>
    apiErrorText(err, tRoot, `instructor.${fallbackKey}`, { namespace: "instructor.errors" });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<{ moduleId: string; title: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [studentData, setStudentData] = useState<StudentDataCounts | null>(null);
  const [gradedWork, setGradedWork] = useState<StudentDataCounts | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [emptiesCourse, setEmptiesCourse] = useState(false);
  // R3-L10: the moved item's button keeps focus, and the move is announced.
  const [announcement, setAnnouncement] = useState("");
  const [focusAfterMove, setFocusAfterMove] = useState<string | null>(null);
  // R3-L11: after a successful delete, focus goes to the curriculum heading.
  const [focusHeading, setFocusHeading] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    if (!focusAfterMove) return;
    listRef.current?.querySelector<HTMLElement>(`[data-testid="${focusAfterMove}"]`)?.focus();
  }, [focusAfterMove, modules, busy]);

  useEffect(() => {
    if (focusHeading === 0) return;
    const list = listRef.current;
    const heading =
      list?.closest("section")?.querySelector<HTMLElement>("h1, h2, h3") ?? list ?? null;
    if (!heading) return;
    if (!heading.hasAttribute("tabindex")) heading.setAttribute("tabindex", "-1");
    heading.focus();
  }, [focusHeading]);

  const needle = filter.trim().toLowerCase();
  // R3-L10: `aria-disabled`, not `disabled` — a disabled button drops keyboard focus.
  const reorderOff = needle.length > 0 || busy;
  const visible = modules.map((mod) => ({
    ...mod,
    lessons: needle
      ? mod.lessons.filter((lesson) =>
          `${lesson.title} ${lesson.subtitle ?? ""}`.toLowerCase().includes(needle),
        )
      : mod.lessons,
  }));

  async function saveOrder(
    path: string,
    orderedIds: string[],
    next: CurriculumModule[],
    moved: { focusTestId: string; position: number; total: number },
  ) {
    setError(null);
    setAnnouncement("");
    setBusy(true);
    setFocusAfterMove(moved.focusTestId);
    const previous = modules;
    onModulesChange(next);
    try {
      const res = await apiFetch(path, { method: "PUT", body: JSON.stringify({ orderedIds }) });
      if (!res.ok) {
        onModulesChange(previous);
        setError(errorText(await readApiErrorResponse(res), "reorderFailed"));
        await onChanged();
      } else {
        setAnnouncement(t("movedToPosition", { position: moved.position, total: moved.total }));
      }
    } catch (err) {
      onModulesChange(previous);
      setError(errorText(err, "reorderFailed"));
    } finally {
      setBusy(false);
    }
  }

  function moveModule(index: number, offset: -1 | 1) {
    const target = index + offset;
    if (reorderOff || target < 0 || target >= modules.length) return;
    const next = swap(modules, index, offset);
    const id = modules[index]!.id;
    void saveOrder(
      `/v1/instructor/courses/${courseId}/modules`,
      next.map((mod) => mod.id),
      next,
      {
        focusTestId: `move-module-${offset < 0 ? "up" : "down"}-${id}`,
        position: target + 1,
        total: next.length,
      },
    );
  }

  function moveLesson(moduleIndex: number, lessonIndex: number, offset: -1 | 1) {
    const mod = modules[moduleIndex]!;
    const target = lessonIndex + offset;
    if (reorderOff || target < 0 || target >= mod.lessons.length) return;
    const lessons = swap(mod.lessons, lessonIndex, offset);
    const next = modules.map((row, i) => (i === moduleIndex ? { ...row, lessons } : row));
    const id = mod.lessons[lessonIndex]!.id;
    void saveOrder(
      `/v1/instructor/courses/${courseId}/modules/${mod.id}/lessons`,
      lessons.map((lesson) => lesson.id),
      next,
      {
        focusTestId: `move-lesson-${offset < 0 ? "up" : "down"}-${id}`,
        position: target + 1,
        total: lessons.length,
      },
    );
  }

  async function saveRename(e?: React.FormEvent) {
    e?.preventDefault();
    if (!renaming) return;
    const title = renaming.title.trim();
    if (!title) return;
    setError(null);
    const res = await apiFetch(`/v1/instructor/courses/${courseId}/modules/${renaming.moduleId}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }).catch((err: unknown) => err);
    if (!(res instanceof Response) || !res.ok) {
      setError(
        errorText(
          res instanceof Response ? await readApiErrorResponse(res) : res,
          "renameModuleFailed",
        ),
      );
      return;
    }
    setRenaming(null);
    await onChanged();
  }

  function openDelete(target: DeleteTarget) {
    setDeleteTarget(target);
    setStudentData(null);
    setGradedWork(null);
    setEmptiesCourse(false);
    setDeleteError(null);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    setDeleteError(null);
    const base = `/v1/instructor/courses/${courseId}/modules/${deleteTarget.moduleId}`;
    const path = deleteTarget.kind === "module" ? base : `${base}/lessons/${deleteTarget.lessonId}`;
    try {
      // The first try never forces; once the warning is on screen, confirming means "do it anyway"
      // (delete the students' data too / leave the published course empty — R3-L12).
      const force = studentData != null || emptiesCourse;
      const res = await apiFetch(force ? `${path}?force=true` : path, { method: "DELETE" });
      if (res.status === 409) {
        const body = (await res.json().catch(() => null)) as {
          error?: { code?: string; counts?: StudentDataCounts; emptiesPublishedCourse?: boolean };
        } | null;
        const counts = body?.error?.counts;
        if (body?.error?.code === "HAS_GRADED_WORK" && counts) {
          setGradedWork(counts);
          return;
        }
        if (body?.error?.code === "HAS_STUDENT_DATA" && counts) {
          setStudentData(counts);
          setEmptiesCourse(body.error.emptiesPublishedCourse === true);
          return;
        }
        if (body?.error?.code === "EMPTIES_PUBLISHED_COURSE") {
          setEmptiesCourse(true);
          return;
        }
        setDeleteError(errorText(body, "deleteItemFailed"));
        return;
      }
      if (!res.ok) {
        setDeleteError(errorText(await readApiErrorResponse(res), "deleteItemFailed"));
        return;
      }
      const done = deleteTarget;
      setDeleteTarget(null);
      onDeleted?.({
        moduleId: done.moduleId,
        lessonId: done.kind === "lesson" ? done.lessonId : undefined,
      });
      await onChanged();
      setFocusHeading((n) => n + 1);
    } catch (err) {
      setDeleteError(errorText(err, "deleteItemFailed"));
    } finally {
      setBusy(false);
    }
  }

  const moveButton =
    "rounded border px-2 py-0.5 text-xs aria-disabled:opacity-40 hover:bg-[var(--surface-3)]";

  return (
    <>
      {needle ? (
        <p
          className="mt-3 text-xs text-[var(--text-muted)]"
          data-testid="reorder-off-while-searching"
        >
          {t("reorderOffWhileSearching")}
        </p>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="mt-3 text-sm text-[var(--danger)]"
          data-testid="curriculum-error"
        >
          {error}
        </p>
      ) : null}
      <p aria-live="polite" className="sr-only" data-testid="reorder-announcement">
        {announcement}
      </p>
      <ul ref={listRef} className="mt-4 space-y-4">
        {visible.map((mod, moduleIndex) => (
          <li
            key={mod.id}
            className="rounded-lg border border-[var(--border)] p-4"
            data-testid={`module-${mod.id}`}
          >
            <div className="flex flex-wrap items-center gap-2">
              {renaming?.moduleId === mod.id ? (
                <form onSubmit={saveRename} className="flex flex-1 flex-wrap items-center gap-2">
                  <input
                    autoFocus
                    aria-label={t("moduleTitleLabel")}
                    data-testid={`rename-module-input-${mod.id}`}
                    value={renaming.title}
                    maxLength={200}
                    onChange={(e) => setRenaming({ moduleId: mod.id, title: e.target.value })}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setRenaming(null);
                    }}
                    className="flex-1 rounded border px-2 py-1 text-sm border-[var(--border-strong)] bg-[var(--surface-1)] text-[var(--text-primary)]"
                  />
                  <button type="submit" className="rounded border px-2 py-1 text-xs">
                    {t("saveModuleTitle")}
                  </button>
                  <button
                    type="button"
                    className="text-xs underline"
                    onClick={() => setRenaming(null)}
                  >
                    {t("cancel")}
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  className="mr-auto font-medium"
                  onClick={() => onSelectModule(mod.id)}
                >
                  {mod.title}
                </button>
              )}
              <button
                type="button"
                className={moveButton}
                data-testid={`move-module-up-${mod.id}`}
                aria-label={t("moveItemUp", { name: mod.title })}
                aria-disabled={reorderOff || moduleIndex === 0}
                onClick={() => moveModule(moduleIndex, -1)}
              >
                ↑
              </button>
              <button
                type="button"
                className={moveButton}
                data-testid={`move-module-down-${mod.id}`}
                aria-label={t("moveItemDown", { name: mod.title })}
                aria-disabled={reorderOff || moduleIndex === visible.length - 1}
                onClick={() => moveModule(moduleIndex, 1)}
              >
                ↓
              </button>
              {renaming?.moduleId === mod.id ? null : (
                <button
                  type="button"
                  className="text-xs underline"
                  data-testid={`rename-module-${mod.id}`}
                  aria-label={t("renameModuleLabel", { name: mod.title })}
                  onClick={() => setRenaming({ moduleId: mod.id, title: mod.title })}
                >
                  {t("renameModule")}
                </button>
              )}
              <button
                type="button"
                className="text-xs text-red-600 underline"
                data-testid={`delete-module-${mod.id}`}
                aria-label={t("deleteModuleLabel", { name: mod.title })}
                onClick={() => openDelete({ kind: "module", moduleId: mod.id, name: mod.title })}
              >
                {t("deleteModule")}
              </button>
            </div>
            <ul className="mt-2 space-y-1 pl-4 text-sm text-[var(--text-muted)]">
              {mod.lessons.map((lesson, lessonIndex) => {
                const materialBadge = studyMaterialCountBadge(lesson.attachmentCount);
                return (
                  <li key={lesson.id} data-testid={`lesson-${lesson.id}`}>
                    <span>
                      {lesson.title}
                      {lesson.subtitle ? ` — ${lesson.subtitle}` : ""} (
                      {label.contentType(lesson.contentType)})
                    </span>
                    {lesson.unlockAt && Date.parse(lesson.unlockAt) > now ? (
                      <span
                        className="ml-2 rounded bg-[var(--warning-soft)] px-1.5 py-0.5 text-xs text-[var(--text-primary)]"
                        data-testid={`unlock-badge-${lesson.id}`}
                      >
                        {t("unlockBadge", { date: formatLocalDateTime(lesson.unlockAt, locale) })}
                      </span>
                    ) : null}
                    {lesson.hasVideo ? (
                      <span className="ml-2 text-green-700 dark:text-green-400">
                        · {t("videoLinked")}
                      </span>
                    ) : null}
                    <LessonCompletionNote lesson={lesson} />
                    {lesson.contentType === "video" || lesson.contentType === "text" ? (
                      <LessonVideoUpload
                        courseId={courseId}
                        lessonId={lesson.id}
                        hasVideo={lesson.hasVideo}
                        otherUploadRunning={uploadRunning}
                        notConfigured={uploadNotConfigured}
                        describedBy={lesson.contentType === "text" ? convertsNoteId : undefined}
                      />
                    ) : null}
                    {lesson.hasBodyHtml ? (
                      <span className="ml-2 text-green-700 dark:text-green-400">
                        {t("notesSaved")}
                      </span>
                    ) : null}
                    {materialBadge ? (
                      <span
                        className="ml-2 text-green-700 dark:text-green-400"
                        data-testid={`lesson-study-material-count-${lesson.id}`}
                      >
                        {materialBadge === "one"
                          ? t("pdfCount", { count: lesson.attachmentCount })
                          : t("pdfCountPlural", { count: lesson.attachmentCount })}
                      </span>
                    ) : null}
                    {lesson.contentType === "text" ||
                    lesson.contentType === "video" ||
                    lesson.contentType === "live_session" ? (
                      <button
                        type="button"
                        className="relative z-10 ml-2 underline"
                        data-testid={`edit-text-lesson-${lesson.id}`}
                        onClick={() => onEditLesson(mod.id, lesson)}
                      >
                        {t("editSession")}
                      </button>
                    ) : null}
                    <span className="ml-2 inline-flex gap-1 align-middle">
                      <button
                        type="button"
                        className={moveButton}
                        data-testid={`move-lesson-up-${lesson.id}`}
                        aria-label={t("moveItemUp", { name: lesson.title })}
                        aria-disabled={reorderOff || lessonIndex === 0}
                        onClick={() => moveLesson(moduleIndex, lessonIndex, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className={moveButton}
                        data-testid={`move-lesson-down-${lesson.id}`}
                        aria-label={t("moveItemDown", { name: lesson.title })}
                        aria-disabled={reorderOff || lessonIndex === mod.lessons.length - 1}
                        onClick={() => moveLesson(moduleIndex, lessonIndex, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="text-xs text-red-600 underline"
                        data-testid={`delete-lesson-${lesson.id}`}
                        aria-label={t("deleteLessonLabel", { name: lesson.title })}
                        onClick={() =>
                          openDelete({
                            kind: "lesson",
                            moduleId: mod.id,
                            lessonId: lesson.id,
                            name: lesson.title,
                          })
                        }
                      >
                        {t("deleteLesson")}
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
      {deleteTarget ? (
        <ConfirmDeleteDialog
          message={
            deleteTarget.kind === "module"
              ? t("deleteModuleConfirm", { name: deleteTarget.name })
              : t("deleteLessonConfirm", { name: deleteTarget.name })
          }
          studentData={studentData}
          gradedWork={gradedWork}
          emptiesPublishedCourse={emptiesCourse}
          error={deleteError}
          busy={busy}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setDeleteTarget(null)}
        />
      ) : null}
    </>
  );
}
